import { useEffect } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownLeft, ArrowUpRight, ArrowLeft, CheckCircle2, Clock, Loader2, XCircle, Printer } from "lucide-react";
import { faxLog, markFaxSeen } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { FaxHealthCard } from "../components/library/FaxHealthCard";

const OUT = { delivered: ["Delivered", CheckCircle2, "text-emerald-700"], failed: ["Failed", XCircle, "text-rose-700"], failure: ["Failed", XCircle, "text-rose-700"] };

const Row = ({ x }) => {
  const inbound = x.direction === "in";
  const [label, Icon, tone] = inbound ? ["Received", CheckCircle2, "text-sky-700"] : OUT[x.status] || ["Sending…", Clock, "text-amber-700"];
  return (
    <li className="flex items-start gap-3 rounded-xl border border-border bg-card px-4 py-3" data-testid={`fax-log-${x.direction}-${x.id}`}>
      <span className={`h-8 w-8 rounded-full grid place-items-center shrink-0 ${inbound ? "bg-sky-500/10 text-sky-700" : "bg-primary/10 text-primary"}`}>{inbound ? <ArrowDownLeft className="h-4 w-4" /> : <ArrowUpRight className="h-4 w-4" />}</span>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 text-sm">
          <span className="font-medium truncate">{inbound ? x.label : `To ${x.label}`}</span>
          {inbound && x.unread && <span className="rounded-full bg-primary text-primary-foreground px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider" data-testid={`fax-log-new-${x.id}`}>New</span>}
          <span className="font-mono text-xs text-muted-foreground">{x.number}</span>
          {x.pages ? <span className="text-xs text-muted-foreground">· {x.pages} page{x.pages === 1 ? "" : "s"}</span> : null}
        </div>
        {!inbound && x.documents?.length > 0 && <div className="text-xs text-muted-foreground truncate">{x.documents.join(" · ")}</div>}
        {x.error && <div className="text-xs text-rose-700">{x.error}</div>}
        <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px]">
          <span className={`inline-flex items-center gap-1 ${tone}`}><Icon className="h-3.5 w-3.5" /> {label}</span>
          {!inbound && x.status === "delivered" && x.trackable && (x.confirmed_at
            ? <span className="text-emerald-700" data-testid={`fax-log-confirmed-${x.id}`}>Office confirmed · {x.confirmed_by}</span>
            : <span className="text-amber-700" data-testid={`fax-log-awaiting-${x.id}`}>Awaiting office confirmation</span>)}
          <span className="text-muted-foreground">{new Date(x.at).toLocaleString()}</span>
          {inbound && <Link to={`/vault?doc=${x.document_id}`} className="text-primary hover:underline">Open document</Link>}
        </div>
      </div>
    </li>
  );
};

export default function FaxLog() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["fax-log"], queryFn: faxLog, refetchInterval: 30000 });
  const hasNew = Boolean(data?.items?.some((x) => x.unread));
  useEffect(() => { if (hasNew) markFaxSeen().then(() => qc.invalidateQueries({ queryKey: ["fax-unread"] })).catch(() => {}); }, [hasNew, qc]);
  return (
    <div className="min-h-screen bg-background">
    <Navbar />
    <div className="max-w-3xl mx-auto px-6 py-10" data-testid="fax-log-page">
      <Link to="/library" className="text-xs text-muted-foreground hover:text-primary inline-flex items-center gap-1"><ArrowLeft className="h-3.5 w-3.5" /> Library</Link>
      <div className="mt-3 text-[10px] uppercase tracking-widest font-mono text-primary flex items-center gap-1.5"><Printer className="h-3.5 w-3.5" /> Fax log</div>
      <h1 className="font-serif text-3xl mt-1">Every fax, in and out</h1>
      <p className="text-sm text-muted-foreground mt-1">Faxes received at the household number are filed automatically; faxes you send to doctors show delivery and whether the office confirmed.</p>
      <FaxHealthCard />
      {isLoading ? <Loader2 className="h-5 w-5 animate-spin text-primary mt-8" /> : (
        <>
          <div className="mt-5 flex gap-4 text-xs text-muted-foreground" data-testid="fax-log-counts"><span><b className="text-foreground">{data.received}</b> received</span><span><b className="text-foreground">{data.sent}</b> sent</span></div>
          {data.items.length === 0 ? <p className="mt-6 text-sm text-muted-foreground" data-testid="fax-log-empty">No faxes yet.</p>
            : <ul className="mt-4 space-y-2">{data.items.map((x) => <Row key={`${x.direction}-${x.id}`} x={x} />)}</ul>}
        </>
      )}
    </div>
    </div>
  );
}
